import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { AsyncSection, Drawer } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import { fetchAnalytics } from "./adTemplateApi";
import { freeMeter, int, relativeDay } from "./formatters";
import AdCardDialog from "./AdCardDialog";
import VariantList from "./VariantList";

const useStyles = makeStyles({
  summary: {
    display: "flex",
    flexWrap: "wrap",
    gap: 18,
    paddingBottom: 14,
    borderBottom: `1px solid ${tokens.divider}`,
  },
  stat: { minWidth: 92 },
  statValue: {
    fontFamily: tokens.fontHeading,
    fontWeight: 700,
    fontSize: 18,
    color: "#232D2F",
    fontVariantNumeric: "tabular-nums",
  },
  statLabel: {
    fontFamily: tokens.fontBody,
    fontSize: 11,
    fontWeight: 600,
    color: tokens.textSecondary,
    marginTop: 1,
  },
  meta: {
    fontFamily: tokens.fontBody,
    fontSize: 12,
    color: tokens.textHint,
    marginTop: 10,
  },
  heading: {
    fontFamily: tokens.fontBody,
    fontSize: 12,
    fontWeight: 700,
    color: tokens.textSecondary,
    margin: "16px 0 6px",
  },
  prompt: {
    fontFamily: tokens.fontBody,
    fontSize: 13,
    color: tokens.textPrimary,
    wordBreak: "break-word",
  },
  group: { padding: "8px 0", borderBottom: `1px solid ${tokens.divider}` },
});

const Stat = ({ classes, value, label }) => (
  <Box className={classes.stat}>
    <Typography component="div" className={classes.statValue}>{value}</Typography>
    <Typography component="div" className={classes.statLabel}>{label}</Typography>
  </Box>
);

export default function PublicLeadDrawer({ lead, onClose }) {
  const classes = useStyles();
  const [variants, setVariants] = useState({ loading: true, items: [], error: null });
  const [openAd, setOpenAd] = useState(null);

  useEffect(() => {
    if (!lead) return undefined;
    let alive = true;
    setVariants({ loading: true, items: [], error: null });
    fetchAnalytics(`public-leads/${lead._id}/variants`).then((r) => {
      if (!alive) return;
      setVariants({
        loading: false,
        items: (r.data && r.data.items) || [],
        error: r.error,
      });
    });
    return () => {
      alive = false;
    };
  }, [lead]);

  const groups = useMemo(() => {
    if (!lead) return [];
    const byTemplate = {};
    variants.items.forEach((variant) => {
      const key = String(variant.templateId);
      if (!byTemplate[key]) byTemplate[key] = [];
      byTemplate[key].push(variant);
    });
    return (lead.templateIds || [])
      .map(String)
      .filter((id) => byTemplate[id])
      .map((id) => ({ templateId: id, variants: byTemplate[id] }));
  }, [lead, variants.items]);

  if (!lead) return null;

  return (
    <Drawer open onClose={onClose} width={660} title={lead.name || lead.phone}>
      <div data-testid="public-lead-drawer">
        <Box className={classes.summary}>
          <Stat
            classes={classes}
            value={freeMeter(lead.generationsUsed || 0, lead.generationsLimit)}
            label="Generations"
          />
          <Stat classes={classes} value={int(lead.templateCount)} label="Prompts done" />
          <Stat classes={classes} value={lead.carousel} label="Carousel" />
          <Stat
            classes={classes}
            value={lead.claimedAt ? relativeDay(lead.claimedAt) : "No"}
            label="Signed up"
          />
        </Box>

        <Typography component="div" className={classes.meta}>
          {lead.phone} · {lead.email || "—"}
          {lead.state ? ` · ${lead.state}` : ""} · {lead.verifiedVia}
          {lead.claimedAt ? ` · Project ${lead.projectName || lead.assistantId}` : ""}
        </Typography>

        <Typography component="div" className={classes.heading}>Last prompt</Typography>
        <Typography component="div" className={classes.prompt}>{lead.prompt || "—"}</Typography>

        <Typography component="div" className={classes.heading}>Generated ads</Typography>
        <AsyncSection
          loading={variants.loading}
          error={variants.error}
          empty={!groups.length}
          emptyTitle="Nothing generated yet"
          emptyDescription="This lead verified but has not finished a generation."
        >
          <Box data-testid="public-lead-variants">
            {groups.map((group, index) => (
              <Box key={group.templateId} className={classes.group}>
                <Typography component="div" className={classes.statLabel}>
                  {`Generation ${index + 1}`}
                </Typography>
                <VariantList variants={group.variants} onSelect={setOpenAd} />
              </Box>
            ))}
          </Box>
        </AsyncSection>

        <AdCardDialog ad={openAd} onClose={() => setOpenAd(null)} />
      </div>
    </Drawer>
  );
}
